import React, { useMemo, useState } from "react";

import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import TableContainer from "@/Components/Common/TableContainer";
import TableContainerNew from "@/Components/Common/TableContainerNew";
import {
  Button as TwButton,
  Card as TwCard,
  CardBody as TwCardBody,
  CardHeader as TwCardHeader,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "@/Components/Common/Tailwind";
import { Badge, Button as UiButton, Card as UiCard, CardContent, CardDescription, CardHeader as UiCardHeader, CardTitle } from "@/Components/ui";
import useConfirmDialog from "@/hooks/useConfirmDialog";

import {
  WEB_BADGE_VARIANTS,
  WEB_BUTTON_VARIANTS,
  WEB_SAMPLE_ROWS,
  WEB_SECTION_DESCRIPTIONS,
  WEB_SECTION_ORDER,
  WEB_TABLE_COLUMNS,
} from "./demoData";
import sharedUiMetadata from "./metadata.generated";

type SharedUiUsageEntry = {
  pageFile: string;
  routePaths: string[];
};

type SharedUiVariantMeta = {
  id: string;
  label: string;
  sourceFiles: string[];
  usages: SharedUiUsageEntry[];
};

type SharedUiComponentMeta = {
  id: string;
  label: string;
  sourceFiles: string[];
  usages: SharedUiUsageEntry[];
  variants?: SharedUiVariantMeta[];
};

const metadataById = new Map(
  (sharedUiMetadata as SharedUiComponentMeta[]).map((entry) => [entry.id, entry])
);

function RouteUsageBadge({ children }: { children: React.ReactNode }) {
  return (
    <Badge
      variant="outline-primary"
      className="border-[#f1c39f] bg-[#fff3e7] text-[#8d4d17] hover:bg-[#fff3e7] hover:text-[#8d4d17]"
    >
      {children}
    </Badge>
  );
}

function RouteUsageCountBadge({ count }: { count: number }) {
  return (
    <Badge
      variant="soft-dark"
      className="bg-[rgba(15,23,42,0.12)] text-[var(--color-dark)] hover:bg-[rgba(15,23,42,0.12)]"
    >
      {count} routed usages
    </Badge>
  );
}

function SourceList({ sources }: { sources: string[] }) {
  return (
    <div className="space-y-2">
      {sources.map((source) => (
        <div
          key={source}
          className="rounded-xl border border-[var(--color-gray-200)] bg-white px-3 py-2 text-xs font-mono text-[var(--color-gray-700)]"
        >
          {source}
        </div>
      ))}
    </div>
  );
}

function UsagePanel({ usages }: { usages: SharedUiUsageEntry[] }) {
  return (
    <div className="max-h-80 overflow-y-auto rounded-xl border border-[var(--color-gray-200)] bg-white">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-[var(--color-gray-200)] bg-[var(--color-gray-100)]">
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-gray-600)]">
              Page File
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-gray-600)]">
              Routes
            </th>
          </tr>
        </thead>
        <tbody>
          {usages.map((usage) => (
            <tr key={usage.pageFile} className="border-b border-[var(--color-gray-200)] last:border-b-0">
              <td className="px-3 py-3 align-top">
                <code className="text-xs text-[var(--color-gray-700)]">{usage.pageFile}</code>
              </td>
              <td className="px-3 py-3 align-top">
                <div className="flex flex-wrap gap-2">
                  {usage.routePaths.map((routePath) => (
                    <RouteUsageBadge key={routePath}>
                      {routePath}
                    </RouteUsageBadge>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetadataBlock({ meta }: { meta: SharedUiComponentMeta }) {
  return (
    <div className="space-y-4">
      <Row className="g-3">
        <Col lg={5}>
          <TwCard>
            <TwCardHeader>Component File Locations</TwCardHeader>
            <TwCardBody>
              <SourceList sources={meta.sourceFiles} />
            </TwCardBody>
          </TwCard>
        </Col>
        <Col lg={7}>
          <TwCard>
            <TwCardHeader>Used Pages ({meta.usages.length})</TwCardHeader>
            <TwCardBody>
              <UsagePanel usages={meta.usages} />
            </TwCardBody>
          </TwCard>
        </Col>
      </Row>
      {meta.variants?.length ? (
        <Row className="g-3">
          {meta.variants.map((variant) => (
            <Col lg={6} key={variant.id}>
              <TwCard>
                <TwCardHeader>{variant.label}</TwCardHeader>
                <TwCardBody className="space-y-3">
                  <SourceList sources={variant.sourceFiles} />
                  <UsagePanel usages={variant.usages} />
                </TwCardBody>
              </TwCard>
            </Col>
          ))}
        </Row>
      ) : null}
    </div>
  );
}

export default function SharedUiDevPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dialogResult, setDialogResult] = useState("No confirm action yet.");
  const [singleDate, setSingleDate] = useState("2026-04-16");
  const [clearableDate, setClearableDate] = useState("2026-04-18");
  const [rangeDates, setRangeDates] = useState<Date[]>([
    new Date("2026-04-15"),
    new Date("2026-04-19"),
  ]);

  const openDialogDemo = useConfirmDialog(
    "Shared confirm pattern demo: continue?",
    () => setDialogResult("Confirmed through useConfirmDialog."),
    () => setDialogResult("Cancelled through useConfirmDialog.")
  );

  const tableRows = useMemo(() => WEB_SAMPLE_ROWS, []);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fff7f1_0%,#ffffff_22%,#fffdf8_100%)] pb-16">
      <Container className="max-w-7xl space-y-6 py-10">
        <div className="rounded-[28px] border border-[var(--color-border-default)] bg-white px-6 py-8 shadow-[0_20px_80px_rgba(0,0,0,0.06)]">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[var(--color-text-muted)]">
                Developer Surface
              </div>
              <h1 className="mt-2 text-4xl font-semibold text-[var(--color-text-primary)]">
                WEB Shared UI Test Page
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-[var(--color-gray-700)]">
                Current amazing_web shared UI inventory at <code>/dev/ui-shared</code>. This
                page renders live demos plus generated routed-page usage metadata from the current
                source graph.
              </p>
            </div>
            <div className="inline-flex rounded-full border border-black bg-black px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-white">
              amazing_web
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {WEB_SECTION_ORDER.map((sectionId) => {
              const meta = metadataById.get(sectionId);
              return (
                <Badge key={sectionId} variant="outline-primary">
                  {meta?.label ?? sectionId}
                </Badge>
              );
            })}
          </div>
        </div>

        {WEB_SECTION_ORDER.map((sectionId) => {
          const meta = metadataById.get(sectionId);
          if (!meta) return null;

          return (
            <div
              key={sectionId}
              className="rounded-[24px] border border-[var(--color-border-default)] bg-white px-5 py-6 shadow-[0_14px_50px_rgba(0,0,0,0.04)] md:px-6"
            >
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-[var(--color-text-primary)]">
                    {meta.label}
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-[var(--color-gray-700)]">
                    {WEB_SECTION_DESCRIPTIONS[sectionId]}
                  </p>
                </div>
                <RouteUsageCountBadge count={meta.usages.length} />
              </div>

              <div className="mt-6 space-y-6">
                {sectionId === "button" ? (
                  <div className="flex flex-wrap gap-3">
                    {WEB_BUTTON_VARIANTS.map((variant) => (
                      <UiButton key={variant} variant={variant as any}>
                        {variant}
                      </UiButton>
                    ))}
                    <TwButton variant="primary">Tailwind Wrapper</TwButton>
                  </div>
                ) : null}

                {sectionId === "badge" ? (
                  <div className="flex flex-wrap gap-3">
                    {WEB_BADGE_VARIANTS.map((variant) => (
                      <Badge key={variant} variant={variant as any}>
                        {variant}
                      </Badge>
                    ))}
                  </div>
                ) : null}

                {sectionId === "card" ? (
                  <Row className="g-3">
                    <Col md={6}>
                      <UiCard className="border-[var(--color-border-default)]">
                        <UiCardHeader>
                          <CardTitle>UI Card</CardTitle>
                          <CardDescription>Current shadcn-style card primitive.</CardDescription>
                        </UiCardHeader>
                        <CardContent>
                          Used for newer UI surfaces that rely on Components/ui.
                        </CardContent>
                      </UiCard>
                    </Col>
                    <Col md={6}>
                      <TwCard animate>
                        <TwCardHeader>Tailwind Card</TwCardHeader>
                        <TwCardBody>
                          Existing Tailwind card wrapper still appears across many customer pages.
                        </TwCardBody>
                      </TwCard>
                    </Col>
                  </Row>
                ) : null}

                {sectionId === "modal" ? (
                  <>
                    <TwButton variant="primary" onClick={() => setModalOpen(true)}>
                      Open Shared Modal
                    </TwButton>
                    <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
                      <ModalHeader toggle={() => setModalOpen(false)}>
                        Shared Tailwind Modal
                      </ModalHeader>
                      <ModalBody>
                        The current web modal surface comes from <code>Components/Common/Tailwind</code>.
                      </ModalBody>
                      <ModalFooter>
                        <TwButton variant="secondary" onClick={() => setModalOpen(false)}>
                          Close
                        </TwButton>
                      </ModalFooter>
                    </Modal>
                  </>
                ) : null}

                {sectionId === "dialog" ? (
                  <div className="space-y-3">
                    <div className="text-sm text-[var(--color-gray-700)]">{dialogResult}</div>
                    <TwButton variant="primary" onClick={openDialogDemo}>
                      Open Confirm Pattern
                    </TwButton>
                  </div>
                ) : null}

                {sectionId === "dropdown" ? (
                  <Dropdown>
                    <DropdownToggle className="btn btn-light">Open Shared Dropdown</DropdownToggle>
                    <DropdownMenu end>
                      <DropdownItem>Inspect usage list</DropdownItem>
                      <DropdownItem>Open source path</DropdownItem>
                      <DropdownItem>Close menu</DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                ) : null}

                {sectionId === "form" ? (
                  <Form className="max-w-2xl">
                    <FormGroup>
                      <Label htmlFor="web-shared-form-title">Title</Label>
                      <Input id="web-shared-form-title" placeholder="Shared UI form demo" />
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="web-shared-form-type">Type</Label>
                      <Input id="web-shared-form-type" type="select" defaultValue="campaign">
                        <option value="campaign">Campaign</option>
                        <option value="alert">Alert</option>
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="web-shared-form-notes">Notes</Label>
                      <Input
                        id="web-shared-form-notes"
                        type="textarea"
                        rows={4}
                        placeholder="Existing Tailwind form group and input usage."
                      />
                    </FormGroup>
                  </Form>
                ) : null}

                {sectionId === "input" ? (
                  <Row className="g-3">
                    <Col md={6}>
                      <Label htmlFor="web-shared-input-text">Text</Label>
                      <Input id="web-shared-input-text" placeholder="Shared text input" />
                    </Col>
                    <Col md={6}>
                      <Label htmlFor="web-shared-input-number">Number</Label>
                      <Input id="web-shared-input-number" type="number" defaultValue={7} />
                    </Col>
                    <Col md={6}>
                      <Label htmlFor="web-shared-input-select">Select</Label>
                      <Input id="web-shared-input-select" type="select" defaultValue="draft">
                        <option value="draft">Draft</option>
                        <option value="live">Live</option>
                      </Input>
                    </Col>
                    <Col md={6}>
                      <Label htmlFor="web-shared-input-check">Checkbox</Label>
                      <div className="pt-2">
                        <Input id="web-shared-input-check" type="checkbox" defaultChecked />
                      </div>
                    </Col>
                  </Row>
                ) : null}

                {sectionId === "table" ? (
                  <Table>
                    <thead>
                      <tr>
                        <th>Primitive</th>
                        <th>Layer</th>
                        <th>State</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Table</td>
                        <td>Tailwind common wrapper</td>
                        <td>
                          <Badge variant="soft-success">Ready</Badge>
                        </td>
                      </tr>
                      <tr>
                        <td>Rows and cells</td>
                        <td>Used by several list/detail surfaces</td>
                        <td>
                          <Badge variant="soft-primary">Live</Badge>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                ) : null}

                {sectionId === "datepicker" ? (
                  <div className="space-y-4">
                    <Row className="g-3">
                      <Col md={4}>
                        <Label htmlFor="web-date-single">Single Date</Label>
                        <CommonDatePicker
                          id="web-date-single"
                          value={singleDate}
                          clearable
                          onChangeValue={setSingleDate}
                        />
                      </Col>
                      <Col md={4}>
                        <Label htmlFor="web-date-range">Range</Label>
                        <CommonDatePicker
                          id="web-date-range"
                          value={rangeDates}
                          mode="range"
                          clearable
                          onChangeDates={setRangeDates}
                        />
                      </Col>
                      <Col md={4}>
                        <Label htmlFor="web-date-clearable">Clearable Date</Label>
                        <CommonDatePicker
                          id="web-date-clearable"
                          value={clearableDate}
                          clearable
                          onChangeValue={setClearableDate}
                        />
                      </Col>
                    </Row>
                    <TwButton variant="secondary" onClick={() => setDateModalOpen(true)}>
                      Open Modal DatePicker
                    </TwButton>
                    <Modal isOpen={dateModalOpen} toggle={() => setDateModalOpen(false)} centered>
                      <ModalHeader toggle={() => setDateModalOpen(false)} showBack>
                        Modal DatePicker
                      </ModalHeader>
                      <ModalBody>
                        <Label htmlFor="web-date-modal-picker">Inside shared modal</Label>
                        <CommonDatePicker
                          id="web-date-modal-picker"
                          value={singleDate}
                          clearable
                          onChangeValue={setSingleDate}
                        />
                      </ModalBody>
                      <ModalFooter>
                        <TwButton variant="secondary" onClick={() => setDateModalOpen(false)}>
                          Close
                        </TwButton>
                      </ModalFooter>
                    </Modal>
                  </div>
                ) : null}

                {sectionId === "paginated-table" ? (
                  <div className="space-y-6">
                    <div>
                      <div className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
                        Client-side Variant: TableContainer
                      </div>
                      <div className="rounded-[20px] border border-[var(--color-gray-200)] p-4">
                        <TableContainer
                          columns={WEB_TABLE_COLUMNS}
                          data={tableRows}
                          customPageSize={5}
                          tableClass="table mb-0"
                          theadClass=""
                          trClass=""
                          thClass=""
                          divClass="table-responsive"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
                        Client-side Variant: TableContainerNew
                      </div>
                      <div className="rounded-[20px] border border-[var(--color-gray-200)] p-4">
                        <TableContainerNew
                          columns={WEB_TABLE_COLUMNS}
                          data={tableRows}
                          customPageSize={5}
                          tableClass="table mb-0"
                          theadClass=""
                          trClass=""
                          thClass=""
                          divClass="table-responsive"
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                <MetadataBlock meta={meta} />
              </div>
            </div>
          );
        })}
      </Container>
    </div>
  );
}
